import { useParams } from 'react-router';
import { useState, useMemo, useEffect } from 'react';
import useAuth from '../../hooks/utils/useAuth';
import { useContentCollection, Tag, ContentCollection } from '../../hooks/api/useContentCollection';
import { useSessionStorage } from '../../hooks/utils/useSessionStorage';
import { useTagGroups, TagGroup } from '../../hooks/api/useTagGroups';
import { ResourceTile } from '../../components/resource-tile';
import { AudioExperienceTile } from '../../components/audio-experience-tile';
import { VideoExperienceTile } from '../../components/video-experience-tile';
import { DropdownPill } from '../../components/dropdown-pill';
import { Header } from '../../components/layout/header';
import { Footer } from '../../components/layout/footer';
import { ContentSection } from '../../components/content-section';
import { HeroSection, HeroSectionSkeleton } from '../../components/hero-section';
import { Experience, Content, Resource } from '../../hooks/api/useExperiences';
import { createLoadMoreHandler } from '../../utils/constants';
import { getTopicCircle } from '../../utils/topic-utils';
import { VerticalCollectionImage } from '../../assets/images/svgs';

type ContentType = undefined | 'video' | 'audio' | 'resources';

const LOAD_MORE_VIDEO_ITEMS = 6;
const LOAD_MORE_AUDIO_ITEMS = 4;
const LOAD_MORE_RESOURCE_ITEMS = 4;

const ViewCollection = () => {
  const { collection_id } = useParams<{ collection_id: string }>();
  const { user } = useAuth();
  const clientId = useSessionStorage('clientId') || user?.caller_role?.active_subscription?.package?.client?.id;
  const collectionId = collection_id ? parseInt(collection_id, 10) : undefined;

  const {
    data: collectionData,
    isLoading: isLoadingCollection,
    error: collectionError,
  } = useContentCollection(collectionId);

  const { data: tagGroupsData } = useTagGroups(!!collectionData);
  const [contentTypeFilter, setContentTypeFilter] = useState<ContentType>(undefined);

  const collectionTagGroups = useMemo(() => {
    if (!collectionData?.tags || !tagGroupsData?.length) return [];

    const collectionTagIds = new Set(collectionData.tags.map((tag: Tag) => tag.id));

    const findTagGroupsWithTags = (tagGroups: TagGroup[]): TagGroup[] => {
      const results: TagGroup[] = [];
      for (const tagGroup of tagGroups) {
        if (tagGroup.tags?.some((tag) => collectionTagIds.has(tag.id))) {
          results.push(tagGroup);
        }
        if (tagGroup.children?.length) {
          results.push(...findTagGroupsWithTags(tagGroup.children));
        }
      }
      return results;
    };

    return findTagGroupsWithTags(tagGroupsData);
  }, [collectionData?.tags, tagGroupsData]);

  // TODO: Implement logic to get the primary tag group based on user preferences _if_ we are allowing multiple tag groups to be applied to a collection
  const primaryTagGroup = useMemo(() => collectionTagGroups[0] || null, [collectionTagGroups]);

  const getInitialItems = useMemo(() => {
    const hasFilter = contentTypeFilter !== undefined;
    return {
      video: hasFilter ? 24 : 6,
      audio: hasFilter ? 16 : 4,
      resources: hasFilter ? 16 : 4,
    };
  }, [contentTypeFilter]);

  const [itemsShown, setItemsShown] = useState({
    videoExperiences: getInitialItems.video,
    audioExperiences: getInitialItems.audio,
    resources: getInitialItems.resources,
  });

  const groupedContent = useMemo(() => {
    if (!collectionData?.items) {
      return {
        listener_audio: [] as Content[],
        client_resources: [] as Resource[],
        global_resources: [] as Resource[],
      };
    }

    const grouped = {
      listener_audio: [] as Content[],
      client_resources: [] as Resource[],
      global_resources: [] as Resource[],
    };

    collectionData.items.forEach((item) => {
      if (item.listener_audio_id && item.data) {
        grouped.listener_audio.push(item.data as Content);
      } else if (item.resource_id && item.data) {
        const resource = item.data as Resource;
        if (resource.client_id) {
          grouped.client_resources.push(resource);
        } else {
          grouped.global_resources.push(resource);
        }
      }
    });

    return grouped;
  }, [collectionData]);

  const videoExperiences = useMemo(
    () => groupedContent.listener_audio.filter((exp) => (exp as Experience | Content).video_id),
    [groupedContent.listener_audio],
  );

  const audioExperiences = useMemo(
    () => groupedContent.listener_audio.filter((exp) => !(exp as Experience | Content).video_id),
    [groupedContent.listener_audio],
  );

  const allResources = useMemo(
    () => [...groupedContent.client_resources, ...groupedContent.global_resources],
    [groupedContent.client_resources, groupedContent.global_resources],
  );

  useEffect(() => {
    setItemsShown({
      videoExperiences: getInitialItems.video,
      audioExperiences: getInitialItems.audio,
      resources: getInitialItems.resources,
    });
  }, [contentTypeFilter, getInitialItems]);

  const loadMoreVideos = createLoadMoreHandler(
    setItemsShown,
    'videoExperiences',
    LOAD_MORE_VIDEO_ITEMS,
    videoExperiences.length,
  );

  const loadMoreAudio = createLoadMoreHandler(
    setItemsShown,
    'audioExperiences',
    LOAD_MORE_AUDIO_ITEMS,
    audioExperiences.length,
  );

  const loadMoreResources = createLoadMoreHandler(
    setItemsShown,
    'resources',
    LOAD_MORE_RESOURCE_ITEMS,
    allResources.length,
  );

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <div className="flex-1">
        <Body
          collectionId={collectionId}
          clientId={clientId}
          isLoadingCollection={isLoadingCollection}
          collectionError={collectionError}
          collectionData={collectionData}
          primaryTagGroup={primaryTagGroup}
          contentTypeFilter={contentTypeFilter}
          setContentTypeFilter={setContentTypeFilter}
          videoExperiences={videoExperiences}
          audioExperiences={audioExperiences}
          allResources={allResources}
          itemsShown={itemsShown}
          groupedContent={groupedContent}
          loadMoreVideos={loadMoreVideos}
          loadMoreAudio={loadMoreAudio}
          loadMoreResources={loadMoreResources}
        />
      </div>
      <Footer />
    </div>
  );
};

interface BodyProps {
  collectionId?: number;
  clientId?: string | number;
  isLoadingCollection: boolean;
  collectionError: unknown;
  collectionData: ContentCollection | undefined;
  primaryTagGroup: TagGroup | null;
  contentTypeFilter: ContentType;
  setContentTypeFilter: (value: ContentType) => void;
  videoExperiences: (Experience | Content)[];
  audioExperiences: (Experience | Content)[];
  allResources: Resource[];
  itemsShown: {
    videoExperiences: number;
    audioExperiences: number;
    resources: number;
  };
  groupedContent: {
    listener_audio: Content[];
    client_resources: Resource[];
    global_resources: Resource[];
  };
  loadMoreVideos: () => void;
  loadMoreAudio: () => void;
  loadMoreResources: () => void;
}

const Body = ({
  collectionId,
  clientId,
  isLoadingCollection,
  collectionError,
  collectionData,
  primaryTagGroup,
  contentTypeFilter,
  setContentTypeFilter,
  videoExperiences,
  audioExperiences,
  allResources,
  itemsShown,
  groupedContent,
  loadMoreVideos,
  loadMoreAudio,
  loadMoreResources,
}: BodyProps) => {
  if (!collectionId || !clientId) {
    return (
      <div className="max-w-page mx-auto px-6 py-8">
        <h1 className="mb-2 font-['Open_Sans'] text-[30px] font-semibold leading-[60px] text-black">View Collection</h1>
        <p>Loading or invalid collection...</p>
      </div>
    );
  }

  if (!isLoadingCollection && (collectionError || !collectionData)) {
    return (
      <div className="max-w-page mx-auto px-6 py-8">
        <div className="text-center text-red-500">Error loading collection. Please try again.</div>
      </div>
    );
  }

  const isLoaded = !isLoadingCollection && collectionData;

  return (
    <>
      {isLoaded ? (
        <HeroSection
          title={
            !collectionData!.verticals.length && collectionData!.tags.length > 0
              ? `Featured Content on ${collectionData!.tags[0].name}`
              : collectionData!.name
          }
          description={collectionData!.description}
          icon={
            collectionData!.verticals?.length > 0
              ? VerticalCollectionImage
              : primaryTagGroup
                ? getTopicCircle(primaryTagGroup)
                : undefined
          }
          iconAlt="Collection icon"
          eyebrowText="COLLECTION"
        />
      ) : (
        <HeroSectionSkeleton />
      )}

      <div className="max-w-page mx-auto px-6 py-8">
        <div className="mb-6">
          <DropdownPill
            label="Content type"
            placeholder="All Content"
            options={[
              ...(videoExperiences.length > 0 ? [{ value: 'video', label: 'Video Stories' }] : []),
              ...(audioExperiences.length > 0 ? [{ value: 'audio', label: 'Audio Stories' }] : []),
              ...(allResources.length > 0 ? [{ value: 'resources', label: 'Resources' }] : []),
            ]}
            selectedValue={contentTypeFilter}
            onSelect={(value) => setContentTypeFilter(value as ContentType)}
          />
        </div>

        <ContentSection
          title="Video Stories"
          items={videoExperiences}
          itemsShown={itemsShown.videoExperiences}
          onLoadMore={loadMoreVideos}
          shouldShow={
            (contentTypeFilter === undefined || contentTypeFilter === 'video') &&
            (isLoadingCollection || videoExperiences.length > 0)
          }
          isLoading={isLoadingCollection}
          renderItem={(experience, index) => (
            <VideoExperienceTile
              key={experience?.id || `skeleton-${index}`}
              experience={experience as Experience | Content | undefined}
              handleScheduleModalOpen={() => {
                console.log('Schedule modal for experience:', experience);
              }}
              isLoading={!experience}
            />
          )}
        />

        <ContentSection
          title="Audio Stories"
          items={audioExperiences}
          itemsShown={itemsShown.audioExperiences}
          onLoadMore={loadMoreAudio}
          shouldShow={
            (contentTypeFilter === undefined || contentTypeFilter === 'audio') &&
            (isLoadingCollection || audioExperiences.length > 0)
          }
          isLoading={isLoadingCollection}
          renderItem={(experience, index) => (
            <AudioExperienceTile
              key={experience?.id || `skeleton-${index}`}
              experience={experience as Experience | Content | undefined}
              isLoading={!experience}
            />
          )}
        />

        <ContentSection
          title="Resources"
          items={allResources}
          itemsShown={itemsShown.resources}
          onLoadMore={loadMoreResources}
          shouldShow={
            (contentTypeFilter === undefined || contentTypeFilter === 'resources') &&
            (isLoadingCollection || allResources.length > 0)
          }
          isLoading={isLoadingCollection}
          renderItem={(resource, index) => (
            <ResourceTile
              key={resource?.id || `skeleton-${index}`}
              resource={resource || undefined}
              id={resource?.id}
              image={resource?.image_url}
              resourceName={resource?.name}
              caption={resource?.description}
              isGlobal={resource ? !resource.client_id : undefined}
              tagGroupNames={(resource as any)?.tag_group_names}
              isLoading={!resource}
            />
          )}
        />

        {!isLoadingCollection &&
          ((contentTypeFilter === undefined &&
            groupedContent.listener_audio.length === 0 &&
            groupedContent.client_resources.length === 0 &&
            groupedContent.global_resources.length === 0) ||
            (contentTypeFilter === 'video' && videoExperiences.length === 0) ||
            (contentTypeFilter === 'audio' && audioExperiences.length === 0) ||
            (contentTypeFilter === 'resources' && allResources.length === 0)) && (
            <div className="text-center text-gray-500">
              <p className="text-lg">No content found in this collection.</p>
              {contentTypeFilter !== undefined && <p className="mt-2 text-sm">Try adjusting your filters.</p>}
            </div>
          )}
      </div>
    </>
  );
};

export default ViewCollection;
